/* eslint-disable react/no-array-index-key */
import React, { useMemo, useState } from 'react'
// import PerfectScrollbar from 'react-perfect-scrollbar'
import { Table as ReactstrapTable } from 'reactstrap'

import Loading from '../Loading'
import HeadRow from './HeadRow'
import Row from './Row'

import { Thead, Tbody, ContainerTable, Tr, Td } from './styles'
// import 'react-perfect-scrollbar/dist/css/styles.css'

/**
 * # Tabela
 *
 * Componente para facilitar a renderização de resultados em tabela. Recebe uma lista em `data`
 * e um conjunto de definições em `schema` definindo o comportamento visual da tabela.
 *
 * @extras Demais propriedades podem ser vistas na tipagem do componente.
 *
 * @example
 * const results = [
 *  {name: "Breno", age: 36},
 *  {name: "Bart", age: 6},
 *  {name: "Gab", age: 3}
 * ]
 *
 * const schema = {
 *  name: {
 *    title: "Nome",
 *    render: rowData => (<strong>{rowData.name}</strong>)
 *  },
 *  age: {
 *    title: "Idade",
 *    render: rowData => `${rowData.age} anos`
 *  }
 * }
 *
 * <Table data={results} schema={mySchema} />
 */

const Table: React.FC<TableProps> = ({
  schema,
  data,
  className,
  total,
  hasHeader,
  striped = false,
  sideBorder,
  smallerRowPadding,
  activeSortKey,
  activeSortDirection,
  onSort,
  freeze = null,
  rowWrapper,
  isLoading,
  isLoadingText = '',
  emptyStateText = ''
}) => {
  const [focusColumn, setFocusColumn] = useState(null)

  const isEmpty = useMemo(() => {
    return !!emptyStateText && !isLoading && !data?.length
  }, [emptyStateText, isLoading, data?.length])

  return (
    <ContainerTable data-freeze={freeze}>
      <ReactstrapTable
        className={`my-0 ${className} ${!!sideBorder && 'sideBorder'}`}
        striped={striped}
        onMouseLeave={() => setFocusColumn(null)}
        // responsive
      >
        {hasHeader && (
          <Thead>
            <HeadRow
              schema={schema}
              sideBorder={sideBorder}
              focusColumn={focusColumn}
              total={total}
              activeSortKey={activeSortKey}
              activeSortDirection={activeSortDirection}
              onSort={onSort}
            />
          </Thead>
        )}

        {rowWrapper ? (
          <Tbody>
            <>
              {isEmpty && (
                <Tr>
                  <Td colSpan={2}>
                    <center>{emptyStateText}</center>
                  </Td>
                </Tr>
              )}
              {React.Children.toArray(
                data.map(rowData =>
                  rowWrapper({
                    id: rowData.id,
                    children: Object.entries(schema).map(([key, col]) => (
                      <td key={key}>{col.render(rowData)}</td>
                    ))
                  })
                )
              )}
              {isLoading && (
                <Tr>
                  <Td colSpan={2}>
                    <Loading onlyLine={true} status={isLoading}>
                      {isLoadingText}
                    </Loading>
                  </Td>
                </Tr>
              )}
            </>
          </Tbody>
        ) : (
          <Tbody>
            {isEmpty && (
              <Tr>
                <Td colSpan={2}>
                  <center>{emptyStateText}</center>
                </Td>
              </Tr>
            )}
            <Row
              data={data}
              schema={schema}
              sideBorder={sideBorder}
              smallerRowPadding={smallerRowPadding}
              onMouseOver={setFocusColumn}
            />
            {isLoading && (
              <Tr>
                <Td colSpan={2}>
                  <Loading onlyLine={true} status={isLoading}>
                    {isLoadingText}
                  </Loading>
                </Td>
              </Tr>
            )}
          </Tbody>
        )}
      </ReactstrapTable>
    </ContainerTable>
  )
}

Table.defaultProps = {
  data: [],
  className: '',
  total: null,
  hasHeader: true,
  sideBorder: false,
  smallerRowPadding: false
}

export default Table
